/** Дублирует server/src/game/respectProgression.js (чистые функции UI). */

export const RESPECT_RANKS = Object.freeze([
  { minLevel: 15, title: 'Вор' },
  { minLevel: 12, title: 'Бродяга' },
  { minLevel: 8, title: 'Босяк' },
  { minLevel: 4, title: 'Мужик' },
  { minLevel: 1, title: 'Первоход' },
]);

export const XP_STEPS = Object.freeze([
  100, 140, 190, 250, 320, 400, 490, 590, 700, 820, 960, 1120, 1300, 1500,
]);
export const MAX_LEVEL = XP_STEPS.length + 1;

const RESPECT_BASE_WIN = 30;
export const RESPECT_BOT_WIN = Math.max(1, Math.round(RESPECT_BASE_WIN * 0.33));

const MILESTONE_META = Object.freeze([
  { id: 'lvl-1', level: 1, chips: 250, bucks: 0, title: 'Валюта x3' },
  { id: 'lvl-2', level: 2, chips: 400, bucks: 0, title: 'Бустер опыта' },
  { id: 'lvl-3', level: 3, chips: 750, bucks: 1, title: 'Rome Colosseum' },
  { id: 'lvl-4', level: 4, chips: 900, bucks: 1, title: 'Золотой билет' },
  { id: 'lvl-5', level: 5, chips: 1500, bucks: 2, title: 'Scratcher x2' },
  { id: 'lvl-6', level: 6, chips: 1800, bucks: 2, title: 'Части кия x3' },
  { id: 'lvl-7', level: 7, chips: 2200, bucks: 2, title: 'Кейс редкий' },
  { id: 'lvl-8', level: 8, chips: 2700, bucks: 3, title: 'Банкролл x2' },
  { id: 'lvl-9', level: 9, chips: 3200, bucks: 3, title: 'Части кия x6' },
  { id: 'lvl-10', level: 10, chips: 4000, bucks: 4, title: 'Эпик кейс' },
  { id: 'lvl-11', level: 11, chips: 5200, bucks: 4, title: 'Монеты x5' },
  { id: 'lvl-12', level: 12, chips: 6800, bucks: 5, title: 'Супер набор' },
  { id: 'lvl-13', level: 13, chips: 8600, bucks: 6, title: 'Турбо бустер' },
  { id: 'lvl-14', level: 14, chips: 11000, bucks: 8, title: 'Мифик кейс' },
  { id: 'lvl-15', level: 15, chips: 15000, bucks: 10, title: 'Легендарный приз' },
]);

function cumulativeAtLevelStart(level) {
  const lvl = Math.max(1, Math.floor(Number(level) || 1));
  let sum = 0;
  for (let l = 1; l < lvl; l++) {
    sum += XP_STEPS[l - 1] || 0;
  }
  return sum;
}

export const TROPHY_ROAD_MILESTONES = Object.freeze(
  MILESTONE_META.map((m) => ({
    ...m,
    trophies: cumulativeAtLevelStart(m.level),
  }))
);

/**
 * Кусочно-линейное сопоставление респекта с позицией по треку, где вехи стоят равномерно
 * (segmentPx = ширина карточки + зазор между центрами соседних колонок).
 */
export function trophyScoreToRoadProgressPx(trophyScoreRaw, segmentPx) {
  const seg = Math.max(0, Number(segmentPx) || 0);
  const milestones = TROPHY_ROAD_MILESTONES;
  const n = milestones.length;
  if (n === 0 || seg === 0) return 0;

  const T = Math.max(0, Math.floor(Number(trophyScoreRaw) || 0));
  const lastT = milestones[n - 1].trophies;
  if (T >= lastT) return (n - 1) * seg;

  const t0 = milestones[0].trophies;
  if (T <= t0) return 0;

  for (let i = 0; i < n - 1; i += 1) {
    const a = milestones[i].trophies;
    const b = milestones[i + 1].trophies;
    if (T <= b) {
      const span = Math.max(1, b - a);
      const frac = (T - a) / span;
      return Math.min((n - 1) * seg, (i + frac) * seg);
    }
  }
  return (n - 1) * seg;
}

function maxCumulativeRespect() {
  return cumulativeAtLevelStart(MAX_LEVEL + 1);
}

function toLevel(rawLevel) {
  const level = Math.floor(Number(rawLevel) || 1);
  if (!Number.isFinite(level)) return 1;
  return Math.max(1, Math.min(MAX_LEVEL, level));
}

export function xpRequiredForLevel(level) {
  const lvl = toLevel(level);
  if (lvl >= MAX_LEVEL) return 0;
  return XP_STEPS[lvl - 1] || XP_STEPS[XP_STEPS.length - 1] || 100;
}

export function rankByLevel(level) {
  const lvl = toLevel(level);
  const found = RESPECT_RANKS.find((item) => lvl >= item.minLevel);
  return found ? found.title : 'Первоход';
}

export function normalizeProgress(levelRaw, xpRaw) {
  let level = toLevel(levelRaw);
  let xp = Math.max(0, Math.floor(Number(xpRaw) || 0));
  while (level < MAX_LEVEL) {
    const need = xpRequiredForLevel(level);
    if (need <= 0 || xp < need) break;
    xp -= need;
    level += 1;
  }
  if (level >= MAX_LEVEL) {
    return { level: MAX_LEVEL, xpCurrent: 0, xpMax: 0, rankName: rankByLevel(MAX_LEVEL) };
  }
  return {
    level,
    xpCurrent: xp,
    xpMax: xpRequiredForLevel(level),
    rankName: rankByLevel(level),
  };
}

export function cumulativeRespectPoints(levelRaw, xpRaw) {
  const norm = normalizeProgress(levelRaw, xpRaw);
  let total = 0;
  for (let l = 1; l < norm.level; l++) {
    total += xpRequiredForLevel(l);
  }
  total += norm.xpCurrent;
  return Math.min(maxCumulativeRespect(), total);
}

function progressFromCumulative(totalRaw) {
  let total = Math.max(0, Math.floor(Number(totalRaw) || 0));
  total = Math.min(maxCumulativeRespect(), total);
  let level = 1;
  let remaining = total;
  while (level < MAX_LEVEL) {
    const need = xpRequiredForLevel(level);
    if (remaining < need) break;
    remaining -= need;
    level += 1;
  }
  if (level >= MAX_LEVEL) {
    return { level: MAX_LEVEL, xpCurrent: 0, xpMax: 0, rankName: rankByLevel(MAX_LEVEL) };
  }
  return {
    level,
    xpCurrent: remaining,
    xpMax: xpRequiredForLevel(level),
    rankName: rankByLevel(level),
  };
}

export function applyRespectDelta(levelRaw, xpRaw, deltaRaw) {
  const delta = Math.trunc(Number(deltaRaw) || 0);
  const norm = normalizeProgress(levelRaw, xpRaw);
  const cumBefore = cumulativeRespectPoints(norm.level, norm.xpCurrent);
  const cumAfter = Math.max(0, cumBefore + delta);
  const next = progressFromCumulative(cumAfter);
  return {
    ...next,
    appliedDelta: delta,
    totalBefore: cumBefore,
    totalAfter: cumAfter,
  };
}

export function calcRespectReward(input) {
  const game = input?.game === 'belot' ? 'belot' : 'seka';
  const didWin = Boolean(input?.didWin);
  const hasBots = Boolean(input?.hasBots);
  const isOnline = Boolean(input?.isOnline);

  if (!isOnline) {
    if (didWin && hasBots) return RESPECT_BOT_WIN;
    return 0;
  }

  if (didWin) {
    if (hasBots) return RESPECT_BOT_WIN;
    return game === 'belot' ? 15 : 10;
  }

  if (hasBots) {
    return game === 'belot' ? -3 : -2;
  }
  return game === 'belot' ? -5 : -3;
}

export function detectUnlockedMilestones(previousScoreRaw, currentScoreRaw) {
  const previousScore = Math.max(0, Math.floor(Number(previousScoreRaw) || 0));
  const currentScore = Math.max(0, Math.floor(Number(currentScoreRaw) || 0));
  if (currentScore <= previousScore) return [];
  return TROPHY_ROAD_MILESTONES.filter((m) => m.trophies > previousScore && m.trophies <= currentScore);
}
